import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  CalendarClock,
  ChevronRight,
  ExternalLink,
  Hash,
  LifeBuoy,
  Loader2,
  LogOut,
  Mail,
  ShieldCheck,
  Smartphone,
  User as UserIcon,
} from 'lucide-react';
import { TRIAL_END_LABEL, getTrialStatus } from './trial';
import { toast } from 'sonner';
import { authService } from '@/services/auth';
import { session } from '@/lib/session';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  isOnCanonicalLoginOrigin,
  redirectToPlatformLogin,
} from '@/lib/platform-login';

/**
 * Native iOS-settings-style profile screen.
 *
 * Layout:
 *   Header card  — large saffron avatar (initials), name, email
 *   "Account" group — Email, Role, Tenant (read-only rows)
 *   "App" group — version, link out to desktop ERP for full profile
 *   Sign out — destructive button at the bottom
 *
 * Each group is a rounded list with subtle internal separators — the
 * same pattern Settings / Notes / Reminders use on iOS.
 */
export function PwaProfilePage() {
  const me = useCurrentUser();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  const initials = makeInitials(me?.fullName ?? me?.email ?? '?');

  const onSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await authService.logout();
    } catch {
      /* swallow */
    }
    session.clear();
    toast.success('Signed out');
    if (!isOnCanonicalLoginOrigin()) {
      redirectToPlatformLogin();
      return;
    }
    navigate('/login', { replace: true });
  };

  return (
    <div className="pwa-anim-in flex flex-col gap-5 px-4 pb-6 pt-4">
      {/* ── Header card ──────────────────────────────────────────── */}
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-border/70 bg-card px-4 py-6 text-center shadow-elev-1">
        <div className="grid h-20 w-20 place-items-center rounded-full bg-gradient-to-br from-primary to-[hsl(22_100%_45%)] text-[26px] font-bold text-primary-foreground shadow-md">
          {initials}
        </div>
        <div className="space-y-0.5">
          <div className="text-[17px] font-semibold leading-tight tracking-tight text-foreground">
            {me?.fullName ?? '—'}
          </div>
          {me?.email && (
            <div className="text-[12.5px] text-muted-foreground">
              {me.email}
            </div>
          )}
        </div>
      </div>

      {/* ── Account ──────────────────────────────────────────────── */}
      <Group label="Account">
        <Row
          icon={<Mail className="h-4 w-4" />}
          label="Email"
          value={me?.email ?? '—'}
        />
        <Row
          icon={<ShieldCheck className="h-4 w-4" />}
          label="Role"
          value={prettyRole(me?.role) ?? '—'}
        />
        <Row
          icon={<Building2 className="h-4 w-4" />}
          label="Tenant"
          value={me?.tenantName ?? me?.tenantId ?? '—'}
        />
      </Group>

      {/* ── App ──────────────────────────────────────────────────── */}
      <Group label="App">
        <LinkRow
          icon={<UserIcon className="h-4 w-4" />}
          label="Full profile on desktop"
          external
          href="https://erp.zafroi.com/settings/profile"
        />
        <LinkRow
          icon={<LifeBuoy className="h-4 w-4" />}
          label="Help & support"
          external
          href="mailto:support@zafroi.com"
        />
        <Row
          icon={<Smartphone className="h-4 w-4" />}
          label="App"
          value="Zafroi Ops"
        />
        <Row
          icon={<Hash className="h-4 w-4" />}
          label="Version"
          value={`${__APP_VERSION__} · ${__BUILD_ID__.slice(-8)}`}
        />
        {(() => {
          const t = getTrialStatus();
          if (t.expired) {
            return (
              <Row
                icon={<CalendarClock className="h-4 w-4" />}
                label="Beta access"
                value="Trial ended — subscription required"
              />
            );
          }
          const daysLine =
            t.daysRemaining === 0
              ? 'Final day'
              : t.daysRemaining === 1
                ? '1 day left'
                : `${t.daysRemaining} days left`;
          return (
            <Row
              icon={<CalendarClock className="h-4 w-4" />}
              label="Beta access"
              value={`${daysLine} · until ${TRIAL_END_LABEL}`}
            />
          );
        })()}
      </Group>

      {/* ── Sign out ─────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={onSignOut}
        disabled={signingOut}
        className="pwa-tap mt-2 inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-destructive/30 bg-destructive/5 text-[14px] font-semibold text-destructive active:bg-destructive/10 disabled:opacity-50"
      >
        {signingOut ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <LogOut className="h-4 w-4" />
            Sign out
          </>
        )}
      </button>
    </div>
  );
}

// ─────────────────────────── primitives ────────────────────────────

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </h2>
      <div className="divide-y divide-border/60 overflow-hidden rounded-2xl border border-border/70 bg-card">
        {children}
      </div>
    </section>
  );
}

function Row({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11.5px] text-muted-foreground">{label}</div>
        <div className="truncate text-[14px] font-medium text-foreground">
          {value}
        </div>
      </div>
    </div>
  );
}

function LinkRow({
  icon,
  label,
  href,
  external,
}: {
  icon: React.ReactNode;
  label: string;
  href: string;
  external?: boolean;
}) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer noopener' : undefined}
      className="pwa-tap flex items-center gap-3 px-4 py-3 text-foreground active:bg-muted/40"
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
        {icon}
      </span>
      <span className="flex-1 truncate text-[14px] font-medium">{label}</span>
      {external ? (
        <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
      ) : (
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
    </a>
  );
}

function makeInitials(input: string): string {
  const cleaned = input.trim();
  if (!cleaned) return '?';
  if (cleaned.includes('@')) {
    return cleaned[0]?.toUpperCase() ?? '?';
  }
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function prettyRole(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return raw
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/(^|\s)\w/g, (m) => m.toUpperCase());
}
